import { ArrowUpRight, Quote, TrendingUp } from "lucide-react";
import tiktokStatsScreenshot from "../images/tiktokStatsScreenshot.PNG"
import twitchStreamReaction from "../images/twitchStreamReaction.jpeg"
import anteos1 from "../images/anteos1.jpg"

const MINT = "#84ff00";
const PURPLE = "#9146FF";
const HAIR = "rgba(255,255,255,0.08)";
const SURF = "rgba(255,255,255,0.035)";

export const caseStudies = [
  {
    name: "Anteos1",
    role: "Owner, Kre8 Content",
    avatar: anteos1,
    label: "CASE STUDY · CONTENT STRATEGY",
    title: "From inconsistent posts to three 100K-view videos",
    story: "“I spent three months posting different types of content, with very few performing well. I needed a way to use data to plan my content strategy for the next few months. I manually aggregated the data in a Notion board, realized how inefficient that was and built a tool to make it more simple. I used that data to plan a content strategy, and my views shot up by 500k+ that month as shown by the screenshot and multiple 2,000+ average viewer streamers reacted to my content LIVE. Data is the source, use it to your advantage.”",
    metrics: [
      { value: "3 months", label: "Testing time" },
      { value: "100K+ views", label: "3 next videos reached", highlight: true },
      { value: "TikTok + Instagram", label: "Reels compared" },
    ],
    disclaimer: "A real personal result, not a promise of future results.",
    images: [
      { src: tiktokStatsScreenshot, alt: "Vertical analytics screenshot 1" },
      { src: twitchStreamReaction, alt: "Vertical analytics screenshot 2" },
    ],
  },
];

function Screenshot({ image, index }) {
  if (image.src) {
    return <figure className="min-w-0">
      <img src={image.src} alt={image.alt} className="aspect-[9/16] w-full rounded-xl border object-cover" style={{ borderColor: HAIR }} />
      {image.caption && <figcaption className="mt-2 text-center text-[11px] leading-relaxed text-white/40">{image.caption}</figcaption>}
    </figure>;
  }

  return <div className="flex aspect-[9/16] min-w-0 flex-col items-center justify-center rounded-xl border px-3 text-center" style={{ borderColor: HAIR, background: "linear-gradient(160deg, rgba(145,70,255,.14), rgba(255,255,255,.025))" }}>
    <TrendingUp aria-hidden="true" className="h-5 w-5" style={{ color: MINT }} />
    <p className="mt-3 text-xs font-semibold text-white/75">Analytics screenshot {index + 1}</p>
    <p className="mt-1 text-[11px] leading-relaxed text-white/40">Add a 9:16 result screenshot here</p>
  </div>;
}

function CaseStudyCard({ study }) {
  const images = study.images?.slice(0, 2) || [];
  return <article className="relative overflow-hidden rounded-3xl border p-5 sm:p-7" style={{ borderColor: HAIR, background: `radial-gradient(60% 90% at 100% 0%, ${PURPLE}26, transparent 74%), ${SURF}` }}>
    <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${PURPLE}, transparent)` }} />
    <div className={`grid gap-8 ${images.length ? "lg:grid-cols-[minmax(0,1fr)_minmax(210px,35%)] lg:items-center" : ""}`}>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: MINT }}>{study.label}</p>
        <h3 className="mt-4 max-w-2xl text-2xl font-semibold tracking-[-0.03em] text-white sm:text-3xl">{study.title}</h3>
        <div className="mt-5 flex gap-3 text-sm leading-relaxed text-white/65 sm:text-base">
          <Quote aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0" style={{ color: "#c9a8ff" }} />
          <p>{study.story}</p>
        </div>

        <dl className="mt-7 grid gap-3 sm:grid-cols-3">
          {study.metrics.map((metric) => <div key={`${metric.value}-${metric.label}`} className="rounded-2xl border px-4 py-4" style={{ borderColor: metric.highlight ? `${MINT}55` : HAIR, backgroundColor: metric.highlight ? `${MINT}0d` : "rgba(255,255,255,.025)" }}>
            <dt className="text-[11px] leading-relaxed text-white/45">{metric.label}</dt>
            <dd className="mt-1 text-base font-semibold tracking-tight text-white" style={{ color: metric.highlight ? MINT : "#fff" }}>{metric.value}</dd>
          </div>)}
        </dl>

        <div className="mt-6 flex items-center gap-3">
          {study.avatar ? <img src={study.avatar} alt={`${study.name}, ${study.role}`} className="h-10 w-10 rounded-full object-cover" /> : <span aria-hidden="true" className="grid h-10 w-10 place-items-center rounded-full text-sm font-bold text-white" style={{ background: `linear-gradient(135deg, ${PURPLE}, #5b2bb0)` }}>{study.name.charAt(0)}</span>}
          <div>
            <p className="text-sm font-semibold text-white">{study.name}</p>
            <p className="text-xs text-white/45">{study.role}</p>
          </div>
        </div>
        <p className="mt-4 text-[11px] text-white/35">{study.disclaimer}</p>
        {study.cta?.url && <a href={study.cta.url} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-white/80 transition hover:text-white">{study.cta.label} <ArrowUpRight aria-hidden="true" className="h-4 w-4" /></a>}
      </div>

      {images.length > 0 && <div className="grid grid-cols-2 gap-3 self-center">{images.map((image, index) => <Screenshot key={`${image.alt}-${index}`} image={image} index={index} />)}</div>}
    </div>
  </article>;
}

export default function CaseStudySection() {
  const multipleStudies = caseStudies.length > 1;
  return <section aria-labelledby="case-studies-heading" className="pt-24 sm:pt-32">
    <div className="mx-auto max-w-2xl text-center">
      <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: MINT }}>Real results, not guesswork</p>
      <h2 id="case-studies-heading" className="mt-3 text-3xl font-semibold tracking-[-0.03em] text-white sm:text-4xl">Use the data. Make a better next move.</h2>
      <p className="mt-3 text-white/55">Social media analytics cannot guarantee a viral post. Comparing what worked, what did not, and why gives you a stronger starting point for the next content decision.</p>
    </div>
    <div className={`mt-10 ${multipleStudies ? "grid gap-5 lg:grid-cols-2" : ""}`}>
      {caseStudies.map((study) => <CaseStudyCard key={`${study.name}-${study.title}`} study={study} />)}
    </div>
  </section>;
}
